import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useCart } from '../context/CartContext';

const API_URL   = process.env.REACT_APP_API_URL   || 'http://localhost:5002/api/v1';
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

const PRODUCT_COLORS = { physical:'#e8a43a', digital:'#4e9cf5', bundle:'#3ecf8e' };

// ── Service Card ──────────────────────────────────────────
function ServiceCard({ service, featured = false, onAddToCart }) {
  const [hover, setHover] = useState(false);
  const [added, setAdded] = useState(false);
  const src        = resolveMedia(service.media_url || service.thumbnail_url);
  const hasPricing = service.is_fixed_price && service.price;

  const handleAdd = (e) => {
    e.preventDefault();
    onAddToCart(service);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      style={{ display:'flex', flexDirection:featured?'row':'column', background:'var(--bg-2)', borderRadius:18, overflow:'hidden', border:`1px solid ${hover?'rgba(232,164,58,0.35)':'var(--border)'}`, transition:'all 0.2s', transform:hover&&!featured?'translateY(-3px)':'none', boxShadow:hover?'0 12px 36px rgba(0,0,0,0.25)':'none' }}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}>

      <Link to={`/marketplace/services/${service.slug}`} style={{ display:'contents', textDecoration:'none' }}>
        <div style={{ height:featured?'auto':180, minHeight:featured?280:'auto', flex:featured?1:'none', background:'var(--bg-3)', position:'relative', overflow:'hidden', flexShrink:0 }}>
          {src
            ? <img src={src} alt={service.name} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.5s', transform:hover?'scale(1.06)':'scale(1)' }} onError={e=>e.target.parentNode.style.background='var(--bg-3)'}/>
            : <div style={{ width:'100%', height:'100%', background:'linear-gradient(135deg, var(--bg-3), rgba(232,164,58,0.06))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem', opacity:0.15 }}>◧</div>
          }
          <div style={{ position:'absolute', inset:0, background:featured?'linear-gradient(to right, transparent 60%, rgba(8,10,13,0.5) 100%)':'linear-gradient(to top, rgba(8,10,13,0.8) 0%, transparent 55%)' }}/>
          {featured && <div style={{ position:'absolute', top:20, left:20 }}><span style={{ fontSize:'0.62rem', fontWeight:700, padding:'4px 12px', borderRadius:20, background:'rgba(232,164,58,0.9)', color:'#080a0d' }}>Featured</span></div>}
          {service.status!=='active' && <span style={{ position:'absolute', top:14, right:14, fontSize:'0.6rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(60,60,60,0.85)', color:'rgba(255,255,255,0.6)' }}>Coming Soon</span>}
        </div>
        <div style={{ padding:featured?'36px 40px':'18px 22px 12px', display:'flex', flexDirection:'column', gap:10, flex:1 }}>
          {service.category_name && <div style={{ fontSize:'0.62rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{service.category_name}</div>}
          <h3 style={{ fontFamily:'var(--font-display)', fontSize:featured?'1.4rem':'1.02rem', fontWeight:600, lineHeight:1.3, color:hover?'var(--gold)':'var(--t-primary)', transition:'color 0.15s' }}>{service.name}</h3>
          <p style={{ fontSize:'0.83rem', color:'var(--t-secondary)', lineHeight:1.65, flex:1 }}>
            {(service.description||'').slice(0,featured?180:100)}{(service.description||'').length>(featured?180:100)?'…':''}
          </p>
          {service.problem_solved && (
            <div style={{ padding:'9px 12px', background:'rgba(255,255,255,0.02)', borderRadius:8, borderLeft:'2px solid var(--gold)', fontSize:'0.78rem', color:'var(--t-muted)', fontStyle:'italic', lineHeight:1.6 }}>
              "{service.problem_solved}"
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:8 }}>
            {service.estimated_timeline && <span style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>⏱ {service.estimated_timeline}</span>}
            {hasPricing && <span style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--gold)' }}>{service.currency||'RWF'} {Number(service.price).toLocaleString()}</span>}
            {!hasPricing && <span style={{ fontSize:'0.72rem', color:'var(--t-muted)', marginLeft:'auto' }}>Quote-based</span>}
          </div>
        </div>
      </Link>

      <div style={{ padding: featured ? '0 40px 36px' : '0 22px 18px', display:'flex', gap:8 }}>
        {hasPricing ? (
          <>
            <Link to={`/marketplace/services/${service.slug}`} style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', fontSize:'0.82rem', textDecoration:'none', textAlign:'center' }}>Details</Link>
            <button onClick={handleAdd} style={{ flex:2, padding:'9px 14px', borderRadius:8, border:'none', background:added?'#3ecf8e':'var(--gold)', color:'#080a0d', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', transition:'all 0.2s', fontFamily:'inherit' }}>
              {added?'✓ Added!':'🛒 Add to Cart'}
            </button>
          </>
        ) : (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <Link to={`/marketplace/services/${service.slug}`}
              style={{ padding:'9px 18px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', fontSize:'0.82rem', textDecoration:'none', whiteSpace:'nowrap' }}>
              View Details
            </Link>
            <Link to={`/contact?type=client&service=${service.slug}`}
              style={{ padding:'9px 18px', borderRadius:8, border:'1px solid rgba(232,164,58,0.3)', background:'rgba(232,164,58,0.06)', color:'var(--gold)', fontSize:'0.82rem', fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}>
              Request Service →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────
function ProductCard({ product, onAddToCart }) {
  const [hover, setHover] = useState(false);
  const [added, setAdded] = useState(false);
  const src        = resolveMedia(product.media_url);
  const color      = PRODUCT_COLORS[product.product_type] || 'var(--gold)';
  const icon       = { physical:'◉', digital:'◈', bundle:'◐' }[product.product_type] || '◈';
  const outOfStock = product.stock_qty === 0;

  const handleAdd = () => {
    onAddToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ display:'flex', flexDirection:'column', background:'var(--bg-2)', borderRadius:18, overflow:'hidden', border:`1px solid ${hover?`${color}50`:'var(--border)'}`, transition:'all 0.2s', transform:hover?'translateY(-3px)':'none', boxShadow:hover?'0 12px 36px rgba(0,0,0,0.25)':'none' }}>
      <Link to={`/marketplace/products/${product.slug}`} style={{ textDecoration:'none' }}>
        <div style={{ height:200, background:'var(--bg-3)', position:'relative', overflow:'hidden', flexShrink:0 }}>
          {src
            ? <img src={src} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.5s', transform:hover?'scale(1.06)':'scale(1)' }} onError={e=>e.target.parentNode.style.background='var(--bg-3)'}/>
            : <div style={{ width:'100%', height:'100%', background:`linear-gradient(135deg, var(--bg-3), ${color}14)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'4rem', opacity:0.18 }}>{icon}</div>
          }
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.75) 0%, transparent 55%)' }}/>
          <div style={{ position:'absolute', top:14, left:14, display:'flex', gap:6 }}>
            <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:`${color}dd`, color:'#080a0d', textTransform:'capitalize' }}>{icon} {product.product_type}</span>
            {product.on_sale && product.discount_pct>0 && <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:'rgba(240,96,96,0.9)', color:'#fff' }}>-{product.discount_pct}%</span>}
          </div>
          {product.is_featured && <span style={{ position:'absolute', top:14, right:14, fontSize:'0.6rem', fontWeight:700, padding:'4px 10px', borderRadius:20, background:'rgba(232,164,58,0.9)', color:'#080a0d' }}>⭐ Featured</span>}
          {outOfStock && (
            <div style={{ position:'absolute', inset:0, background:'rgba(8,10,13,0.72)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--t-muted)', padding:'8px 18px', background:'var(--bg-2)', borderRadius:8 }}>Out of Stock</span>
            </div>
          )}
        </div>
        <div style={{ padding:'18px 20px 12px', display:'flex', flexDirection:'column', gap:8, flex:1 }}>
          {product.category && <div style={{ fontSize:'0.62rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{product.category}</div>}
          <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.02rem', fontWeight:600, lineHeight:1.3, color:hover?color:'var(--t-primary)', transition:'color 0.15s' }}>{product.name}</h3>
          <p style={{ fontSize:'0.83rem', color:'var(--t-secondary)', lineHeight:1.65 }}>
            {(product.short_desc||product.description||'').slice(0,90)}{(product.short_desc||product.description||'').length>90?'…':''}
          </p>
          {product.tech_stack?.length>0 && (
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {product.tech_stack.slice(0,4).map(t=><span key={t} style={{ fontSize:'0.6rem', padding:'3px 8px', borderRadius:20, background:'var(--bg-3)', color:'var(--t-muted)', border:'1px solid var(--border)' }}>{t}</span>)}
            </div>
          )}
        </div>
      </Link>
      <div style={{ padding:'10px 20px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, borderTop:'1px solid var(--border)', marginTop:'auto' }}>
        <div>
          {product.is_free
            ? <span style={{ fontSize:'1.1rem', fontWeight:700, color:'#3ecf8e' }}>Free</span>
            : <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
                <span style={{ fontSize:'1.1rem', fontWeight:700, color }}>{product.currency} {Number(product.price).toLocaleString()}</span>
                {product.on_sale && product.compare_price && <span style={{ fontSize:'0.75rem', color:'var(--t-muted)', textDecoration:'line-through' }}>{Number(product.compare_price).toLocaleString()}</span>}
              </div>
          }
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Link to={`/marketplace/products/${product.slug}`} style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', fontSize:'0.8rem', textDecoration:'none', display:'flex', alignItems:'center' }}>Details</Link>
          <button onClick={handleAdd} disabled={outOfStock}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:added?'#3ecf8e':color, color:'#080a0d', fontSize:'0.8rem', fontWeight:700, cursor:outOfStock?'not-allowed':'pointer', opacity:outOfStock?0.5:1, transition:'all 0.2s', fontFamily:'inherit', whiteSpace:'nowrap' }}>
            {added?'✓ Added!':product.is_free?'Get Free':'🛒 Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────
export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate  = useNavigate();
  const { addItem, count, setDrawerOpen } = useCart();
  const tab = searchParams.get('tab') || 'services';

  const [services,   setServices]   = useState([]);
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeType, setActiveType] = useState('');

  const setTab = (t) => setSearchParams({ tab:t });

  const loadServices = useCallback(async () => {
    setLoading(true);
    try { const res = await publicAPI.services?.(); setServices(res?.data?.data || []); }
    catch { setServices([]); } finally { setLoading(false); }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/products${activeType?`?type=${activeType}`:''}`;
      const res = await fetch(url);
      const data = await res.json();
      setProducts(data.data || []);
    } catch { setProducts([]); } finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => {
    if (tab==='services') loadServices();
    else loadProducts();
  }, [tab, loadServices, loadProducts]);

  const handleAddProduct = (product) => {
    addItem({ id:product.id, name:product.name, type:product.product_type, price:product.is_free?0:Number(product.price), currency:product.currency||'RWF', image:product.media_url, slug:product.slug, is_free:product.is_free });
  };

  const handleAddService = (service) => {
    addItem({ id:service.id, name:service.name, type:'service', price:Number(service.price), currency:service.currency||'RWF', image:service.media_url||service.thumbnail_url, slug:service.slug });
  };

  const featured     = services.slice(0,1);
  const rest         = services.slice(1);
  const productTypes = [...new Set(products.map(p=>p.product_type))];

  const tabBtn = (t) => ({
    padding:'11px 24px', fontSize:'0.9rem', cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--gold)':'transparent'}`,
    color:tab===t?'var(--gold)':'var(--t-secondary)', fontWeight:tab===t?700:400,
    fontFamily:tab===t?'var(--font-display)':'inherit', transition:'all 0.15s', marginBottom:-1,
  });

  return (
    <div>
      {/* Hero */}
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom:'clamp(48px,6vw,80px)', background:'linear-gradient(160deg, var(--bg-1) 0%, var(--bg-0) 70%)', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, right:0, width:'45%', height:'100%', background:'linear-gradient(135deg, transparent 40%, rgba(232,164,58,0.04) 100%)', pointerEvents:'none' }}/>
        <div className="container" style={{ position:'relative', zIndex:1 }}>
          <div className="section-label label" style={{ marginBottom:16 }}>MilleHills Marketplace</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,5vw,3.2rem)', fontWeight:700, lineHeight:1.15, marginBottom:16 }}>
            Build. Buy. <span style={{ color:'var(--gold)', fontStyle:'italic' }}>Grow.</span>
          </h1>
          <p style={{ color:'var(--t-secondary)', fontSize:'1rem', maxWidth:520, lineHeight:1.8, marginBottom:32 }}>
            Professional services and products built by the MilleHills team — everything you need to grow digitally.
          </p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {[
              { icon:'◧', label:'Web & Mobile Dev',    color:'var(--gold)' },
              { icon:'◈', label:'Digital Products',    color:'#4e9cf5' },
              { icon:'◉', label:'Branded Merchandise', color:'#3ecf8e' },
              { icon:'◐', label:'Starter Bundles',     color:'#a78bfa' },
            ].map(p=>(
              <div key={p.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 16px', background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:10, fontSize:'0.83rem', color:'var(--t-secondary)' }}>
                <span style={{ color:p.color }}>{p.icon}</span>{p.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab bar with cart */}
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container">
          <div style={{ display:'flex', alignItems:'center' }}>

            {/* Tabs + Cart inline */}
            <button style={tabBtn('services')} onClick={()=>setTab('services')}>
              Services <span style={{ marginLeft:6, fontSize:'0.62rem', padding:'2px 7px', borderRadius:10, background:'rgba(232,164,58,0.12)', color:'var(--gold)' }}>For Business</span>
            </button>
            <button style={tabBtn('products')} onClick={()=>setTab('products')}>
              Products <span style={{ marginLeft:6, fontSize:'0.62rem', padding:'2px 7px', borderRadius:10, background:'rgba(78,156,245,0.12)', color:'#4e9cf5' }}>Shop</span>
            </button>

            {/* Cart — sits right next to Products */}
            <button onClick={()=>setDrawerOpen(true)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', marginLeft:8, borderRadius:8, background:count>0?'rgba(232,164,58,0.1)':'transparent', border:`1px solid ${count>0?'rgba(232,164,58,0.35)':'var(--border)'}`, cursor:'pointer', transition:'all 0.15s', color:count>0?'var(--gold)':'var(--t-muted)', fontFamily:'inherit', fontSize:'0.82rem', fontWeight:count>0?600:400 }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(232,164,58,0.1)'; e.currentTarget.style.borderColor='rgba(232,164,58,0.35)'; e.currentTarget.style.color='var(--gold)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=count>0?'rgba(232,164,58,0.1)':'transparent'; e.currentTarget.style.borderColor=count>0?'rgba(232,164,58,0.35)':'var(--border)'; e.currentTarget.style.color=count>0?'var(--gold)':'var(--t-muted)'; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              Cart
              {count>0 && (
                <span style={{ minWidth:17, height:17, borderRadius:9, background:'var(--gold)', color:'#080a0d', fontSize:'0.58rem', fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'0 4px', lineHeight:1 }}>
                  {count>99?'99+':count}
                </span>
              )}
            </button>
          </div>

          {/* Product type filters */}
          {tab==='products' && productTypes.length>0 && (
            <div style={{ padding:'10px 0 12px', display:'flex', gap:8 }}>
              <button onClick={()=>setActiveType('')} style={{ padding:'5px 14px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontWeight:!activeType?700:400, background:!activeType?'var(--gold)':'var(--bg-2)', color:!activeType?'#080a0d':'var(--t-secondary)', border:`1px solid ${!activeType?'var(--gold)':'var(--border)'}`, transition:'all 0.15s' }}>All</button>
              {productTypes.map(t=>(
                <button key={t} onClick={()=>setActiveType(t)} style={{ padding:'5px 14px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontWeight:activeType===t?700:400, background:activeType===t?(PRODUCT_COLORS[t]||'var(--gold)'):'var(--bg-2)', color:activeType===t?'#080a0d':'var(--t-secondary)', border:`1px solid ${activeType===t?(PRODUCT_COLORS[t]||'var(--gold)'):'var(--border)'}`, transition:'all 0.15s', textTransform:'capitalize' }}>
                  {{physical:'◉',digital:'◈',bundle:'◐'}[t]} {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <section style={{ padding:'clamp(40px,6vw,72px) 0', minHeight:'60vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>Loading…</div>
          ) : tab==='services' ? (
            services.length===0 ? (
              <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>No services found</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
                {featured.map(s=><ServiceCard key={s.id} service={s} featured onAddToCart={handleAddService}/>)}
                {rest.length>0 && <div style={{ fontSize:'0.82rem', color:'var(--t-muted)', paddingTop:8 }}>{rest.length} more service{rest.length!==1?'s':''}</div>}
                {rest.length>0 && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:20 }}>
                    {rest.map(s=><ServiceCard key={s.id} service={s} onAddToCart={handleAddService}/>)}
                  </div>
                )}
                <div style={{ marginTop:16, padding:'28px 36px', background:'linear-gradient(135deg, var(--bg-2), rgba(232,164,58,0.04))', border:'1px solid var(--border)', borderRadius:20, display:'flex', alignItems:'center', justifyContent:'space-between', gap:24, flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:600, marginBottom:6 }}>Need something custom?</div>
                    <div style={{ fontSize:'0.88rem', color:'var(--t-secondary)' }}>Tell us what you need — we'll put together a tailored proposal.</div>
                  </div>
                  <Link to="/contact?type=client" className="btn btn-primary" style={{ flexShrink:0 }}>Get a Free Quote →</Link>
                </div>
              </div>
            )
          ) : (
            products.length===0 ? (
              <div style={{ textAlign:'center', padding:80 }}>
                <div style={{ fontSize:'2.5rem', opacity:0.2, marginBottom:12 }}>◈</div>
                <div style={{ color:'var(--t-muted)' }}>No products yet — check back soon</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
                <div style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>{products.length} product{products.length!==1?'s':''} available</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:20 }}>
                  {products.map(p=><ProductCard key={p.id} product={p} onAddToCart={handleAddProduct}/>)}
                </div>
                <div style={{ marginTop:8, padding:'24px 32px', background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:20, flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', fontWeight:600, marginBottom:4 }}>Want a custom build?</div>
                    <div style={{ fontSize:'0.85rem', color:'var(--t-secondary)' }}>We build web apps, mobile apps and digital solutions to order.</div>
                  </div>
                  <Link to="/contact" className="btn btn-primary" style={{ flexShrink:0 }}>Commission a Build →</Link>
                </div>
              </div>
            )
          )}
        </div>
      </section>
    </div>
  );
}
